import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home } from 'lucide-react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import PageContainer from '../layout/PageContainer';


interface MenuItem {
  id: string;
  category: string;
  name: string;
  price: number;
  stock: number;
  active: boolean;
  hasRecipe?: boolean;
  is_takeout_available?: boolean;
  service_type?: 'eatin_only' | 'takeout_only' | 'both';
}

interface Recipe {
  id?: string;
  menuId: string;
  ingredients: RecipeIngredient[];
}

interface RecipeIngredient {
  inventoryId: string;
  itemName: string;
  quantity: number;
  unit: string;
}

interface InventoryItem {
  id: string;
  item_name: string;
  unit: string;
}

const MenuManagement: React.FC = () => {
  const navigate = useNavigate();
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState<Partial<MenuItem>>({
    id: '',
    category: '',
    name: '',
    price: 0,
    stock: 999999, // デフォルト値を実質無制限に設定（2026-02-03）
    active: true,
    is_takeout_available: true,
    service_type: 'both'
  });
  
  // レシピ関連のstate
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [currentRecipe, setCurrentRecipe] = useState<Recipe>({
    menuId: '',
    ingredients: []
  });
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);

  useEffect(() => {
    fetchMenus();
    fetchCategories();
    fetchInventoryItems();
  }, []);

  const fetchMenus = async () => {
    try {
      // 管理画面では販売停止中のメニューも含めて全て取得
      const response = await apiClient.get('/menus');
      console.log('取得したメニュー数:', response.data.length);
      console.log('active=0のメニュー:', response.data.filter((m: MenuItem) => !m.active).map((m: MenuItem) => m.name));
      setMenus(response.data);
    } catch (error) {
      console.error('メニューデータ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/menus/categories');
      setCategories(['all', ...response.data]);
    } catch (error) {
      console.error('カテゴリ取得エラー:', error);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const response = await apiClient.get('/inventory');
      setInventoryItems(response.data.map((item: any) => ({
        id: item.id,
        item_name: item.item_name,
        unit: item.unit
      })));
    } catch (error) {
      console.error('在庫アイテム取得エラー:', error);
    }
  };

  const fetchRecipe = async (menuId: string) => {
    try {
      const response = await apiClient.get(`/menus/${menuId}/recipe`);
      return response.data;
    } catch (error) {
      // APIが未実装の場合、ローカルストレージから取得
      console.warn('レシピAPI未実装のため、ローカルストレージから取得:', menuId);
      
      const recipeKey = `recipe_${menuId}`;
      const savedRecipe = localStorage.getItem(recipeKey);
      
      if (savedRecipe) {
        try {
          const parsedRecipe = JSON.parse(savedRecipe);
          console.info('ローカルストレージからレシピを取得:', parsedRecipe);
          return parsedRecipe;
        } catch (parseError) {
          console.error('ローカルストレージのレシピ解析エラー:', parseError);
        }
      }
      
      return {
        id: null,
        menuId: menuId,
        ingredients: []
      };
    }
  };

  const handleOpenRecipeModal = async (menuItem: MenuItem) => {
    const recipe = await fetchRecipe(menuItem.id);
    setCurrentRecipe({
      id: recipe?.id,
      menuId: menuItem.id,
      ingredients: recipe?.ingredients || []
    });
    setShowRecipeModal(true);
  };

  const handleSaveRecipe = async () => {
    try {
      // APIが実装されるまではローカルストレージに保存
      const recipeKey = `recipe_${currentRecipe.menuId}`;
      const recipeData = {
        ...currentRecipe,
        id: currentRecipe.id || `recipe_${Date.now()}`,
        savedAt: new Date().toISOString()
      };
      
      localStorage.setItem(recipeKey, JSON.stringify(recipeData));
      
      // メニューのhasRecipeフラグを更新（ローカル）
      setMenus(menus.map(menu => 
        menu.id === currentRecipe.menuId 
          ? { ...menu, hasRecipe: true }
          : menu
      ));
      
      setShowRecipeModal(false);
      alert('レシピを一時保存しました（APIが実装されるまで）');
      console.info('レシピをローカルストレージに保存:', recipeData);
      
      // 実際のAPI呼び出し（エラー時はスキップ）
      try {
        if (currentRecipe.id) {
          await apiClient.put(`/menus/${currentRecipe.menuId}/recipe`, currentRecipe);
        } else {
          await apiClient.post(`/menus/${currentRecipe.menuId}/recipe`, currentRecipe);
        }
        await fetchMenus(); // メニューリストを再取得（hasRecipe状態を更新）
        console.log('レシピがサーバーに正常に保存されました');
      } catch (apiError) {
        console.warn('サーバーAPI未実装のため、ローカル保存のみ実行:', apiError);
      }
      
    } catch (error) {
      console.error('レシピ保存エラー:', error);
      alert('レシピの保存に失敗しました');
    }
  };

  const handleAddIngredient = () => {
    setCurrentRecipe({
      ...currentRecipe,
      ingredients: [
        ...currentRecipe.ingredients,
        { inventoryId: '', itemName: '', quantity: 0, unit: '' }
      ]
    });
  };

  const handleUpdateIngredient = (index: number, field: keyof RecipeIngredient, value: any) => {
    const updatedIngredients = [...currentRecipe.ingredients];
    updatedIngredients[index] = { ...updatedIngredients[index], [field]: value };
    
    // 在庫アイテムが選択された場合、名前と単位を自動設定
    if (field === 'inventoryId') {
      const selectedItem = inventoryItems.find(item => item.id === value);
      if (selectedItem) {
        updatedIngredients[index].itemName = selectedItem.item_name;
        updatedIngredients[index].unit = selectedItem.unit;
      }
    }
    
    setCurrentRecipe({
      ...currentRecipe,
      ingredients: updatedIngredients
    });
  };

  const handleRemoveIngredient = (index: number) => {
    setCurrentRecipe({
      ...currentRecipe,
      ingredients: currentRecipe.ingredients.filter((_, i) => i !== index)
    });
  };

  const handleAddMenu = async () => {
    if (!newItem.id || !newItem.name || !newItem.category || !newItem.price) {
      alert('必須項目を入力してください');
      return;
    }

    try {
      await apiClient.post('/menus', { ...newItem, category: typeof newItem.category === 'string' ? newItem.category.trim() : newItem.category });
      await fetchMenus();
      await fetchCategories(); // カテゴリータブも更新
      setShowAddModal(false);
      setNewItem({
        id: '',
        category: '',
        name: '',
        price: 0,
        stock: 0,
        active: true,
        is_takeout_available: true,
        service_type: 'both'
      });
      alert('メニューを追加しました');
    } catch (error) {
      console.error('メニュー追加エラー:', error);
      alert('メニューの追加に失敗しました');
    }
  };

  const handleUpdateMenu = async () => {
    if (!editingItem) return;

    try {
      await apiClient.put(`/menus/${editingItem.id}`, {
        category: typeof editingItem.category === 'string' ? editingItem.category.trim() : editingItem.category,
        name: editingItem.name,
        price: editingItem.price,
        stock: editingItem.stock,
        active: editingItem.active,
        is_takeout_available: editingItem.is_takeout_available,
        service_type: editingItem.service_type
      });
      await fetchMenus();
      await fetchCategories(); // カテゴリータブも更新
      setEditingItem(null);
      alert('メニューを更新しました');
    } catch (error) {
      console.error('メニュー更新エラー:', error);
      alert('メニューの更新に失敗しました');
    }
  };

  const handleToggleActive = async (item: MenuItem) => {
    try {
      await apiClient.put(`/menus/${item.id}`, {
        active: !item.active
      });
      await fetchMenus();
    } catch (error) {
      console.error('ステータス更新エラー:', error);
    }
  };

  const handleDeleteMenu = async (id: string) => {
    if (!window.confirm('このメニューを削除しますか？')) return;

    try {
      await apiClient.delete(`/menus/${id}`);
      await fetchMenus();
      await fetchCategories(); // カテゴリータブも更新
      alert('メニューを削除しました');
    } catch (error) {
      console.error('メニュー削除エラー:', error);
      alert('メニューの削除に失敗しました');
    }
  };

  const handleBulkServiceTypeUpdate = async (category: string, serviceType: 'eatin_only' | 'takeout_only' | 'both') => {
    const serviceTypeText = {
      'eatin_only': '店内専用',
      'takeout_only': 'テイクアウト専用',
      'both': '店内・テイクアウト両方'
    };

    if (!window.confirm(`${category}カテゴリの全メニューを「${serviceTypeText[serviceType]}」に設定しますか？`)) return;

    try {
      const categoryMenus = menus.filter(item => item.category === category);
      const menuIds = categoryMenus.map(menu => menu.id);

      await apiClient.patch('/menus/bulk-update', {
        field: 'service_type',
        value: serviceType,
        menuIds: menuIds
      });

      await fetchMenus();
      alert(`${category}カテゴリの提供方法を「${serviceTypeText[serviceType]}」に更新しました`);
    } catch (error) {
      console.error('一括更新エラー:', error);
      alert('提供方法の更新に失敗しました');
    }
  };

  const filteredMenus = selectedCategory === 'all' 
    ? menus 
    : menus.filter(item => item.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <PageContainer>
      <div>
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">メニュー管理</h1>
            <p className="text-gray-600 mt-2">メニューの追加・編集・価格変更</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Home className="w-5 h-5" />
              ホーム
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              新規メニュー追加
            </button>
          </div>
        </div>

      {/* カテゴリフィルター */}
      <div className="mb-6">
        <div className="flex space-x-2 mb-3">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-md transition-colors ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {category === 'all' ? '全て' : category}
            </button>
          ))}
        </div>

        {/* カテゴリ一括操作 */}
        {selectedCategory !== 'all' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-700">
                「{selectedCategory}」カテゴリの提供方法を一括変更：
              </span>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleBulkServiceTypeUpdate(selectedCategory, 'both')}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                >
                  🍽️🥡 全て両方可能
                </button>
                <button
                  onClick={() => handleBulkServiceTypeUpdate(selectedCategory, 'eatin_only')}
                  className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors"
                >
                  🍽️ 全て店内専用
                </button>
                <button
                  onClick={() => handleBulkServiceTypeUpdate(selectedCategory, 'takeout_only')}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                >
                  🥡 全てテイクアウト専用
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* メニューテーブル */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                カテゴリ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                メニュー名
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                価格
              </th>
              {/* 在庫列 - 非表示（2026-02-03）
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                在庫
              </th>
              */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                状態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                テイクアウト
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                レシピ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredMenus.map(item => (
              <tr key={item.id} className={`hover:bg-gray-50 ${!item.active ? 'bg-gray-100 opacity-60' : ''}`}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ¥{item.price.toLocaleString()}
                </td>
                {/* 在庫列 - 非表示（2026-02-03）
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.stock}
                </td>
                */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleActive(item)}
                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      item.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {item.active ? '販売中' : '停止中'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    item.service_type === 'both' ? 'bg-green-100 text-green-800' :
                    item.service_type === 'eatin_only' ? 'bg-gray-100 text-gray-600' :
                    item.service_type === 'takeout_only' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {item.service_type === 'both' ? '🍽️🥡 両方' :
                     item.service_type === 'eatin_only' ? '🍽️ 店内専用' :
                     item.service_type === 'takeout_only' ? '🥡 テイク専用' :
                     '🍽️🥡 両方'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleOpenRecipeModal(item)}
                    className={`px-3 py-1 text-xs rounded-full ${
                      item.hasRecipe
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {item.hasRecipe ? '🍽️ 設定済み' : '📝 未設定'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => setEditingItem(item)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    編集
                  </button>
                  <button
                    onClick={() => handleDeleteMenu(item.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    削除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 新規追加モーダル */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">新規メニュー追加</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">メニューID</label>
                <input
                  type="text"
                  value={newItem.id}
                  onChange={(e) => setNewItem({ ...newItem, id: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  placeholder="T001"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">カテゴリ</label>
                <input
                  type="text"
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  placeholder="とんかつ"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">メニュー名</label>
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  placeholder="ロースかつ定食"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">価格</label>
                <input
                  type="number"
                  value={newItem.price}
                  onChange={(e) => setNewItem({ ...newItem, price: parseInt(e.target.value) || 0 })}
                  className="w-full p-2 border rounded-md"
                  min="0"
                />
              </div>

              {/* 在庫数入力欄 - 非表示（2026-02-03）
              <div>
                <label className="block text-sm font-medium mb-1">在庫数</label>
                <input
                  type="number"
                  value={newItem.stock}
                  onChange={(e) => setNewItem({ ...newItem, stock: parseInt(e.target.value) || 0 })}
                  className="w-full p-2 border rounded-md"
                  min="0"
                />
              </div>
              */}

              <div>
                <label className="block text-sm font-medium mb-1">提供方法</label>
                <select
                  value={newItem.service_type || 'both'}
                  onChange={(e) => setNewItem({ ...newItem, service_type: e.target.value as any })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="both">🍽️🥡 店内・テイクアウト両方</option>
                  <option value="eatin_only">🍽️ 店内専用</option>
                  <option value="takeout_only">🥡 テイクアウト専用</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewItem({
                    id: '',
                    category: '',
                    name: '',
                    price: 0,
                    stock: 0,
                    active: true,
                    is_takeout_available: true,
                    service_type: 'both'
                  });
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                キャンセル
              </button>
              <button
                onClick={handleAddMenu}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                追加
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 編集モーダル */}
      {editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">メニュー編集</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">メニューID</label>
                <input
                  type="text"
                  value={editingItem.id}
                  disabled
                  className="w-full p-2 border rounded-md bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">カテゴリ</label>
                <input
                  type="text"
                  value={editingItem.category}
                  onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                  className="w-full p-2 border rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">メニュー名</label>
                <input
                  type="text"
                  value={editingItem.name}
                  onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                  className="w-full p-2 border rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">価格</label>
                <input
                  type="number"
                  value={editingItem.price}
                  onChange={(e) => setEditingItem({ ...editingItem, price: parseInt(e.target.value) || 0 })}
                  className="w-full p-2 border rounded-md"
                  min="0"
                />
              </div>

              {/* 在庫数入力欄 - 非表示（2026-02-03）
              <div>
                <label className="block text-sm font-medium mb-1">在庫数</label>
                <input
                  type="number"
                  value={editingItem.stock}
                  onChange={(e) => setEditingItem({ ...editingItem, stock: parseInt(e.target.value) || 0 })}
                  className="w-full p-2 border rounded-md"
                  min="0"
                />
              </div>
              */}

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingItem.active}
                    onChange={(e) => setEditingItem({ ...editingItem, active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium">販売中</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">提供方法</label>
                <select
                  value={editingItem.service_type || 'both'}
                  onChange={(e) => setEditingItem({ ...editingItem, service_type: e.target.value as any })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="both">🍽️🥡 店内・テイクアウト両方</option>
                  <option value="eatin_only">🍽️ 店内専用</option>
                  <option value="takeout_only">🥡 テイクアウト専用</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingItem(null)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                キャンセル
              </button>
              <button
                onClick={handleUpdateMenu}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                更新
              </button>
            </div>
          </div>
        </div>
      )}

      {/* レシピ編集モーダル */}
      {showRecipeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-2xl max-w-4xl max-h-[80vh] overflow-auto">
            <h2 className="text-xl font-bold mb-4">
              レシピ設定: {menus.find(m => m.id === currentRecipe.menuId)?.name}
            </h2>
            
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">原材料一覧</h3>
                <button
                  onClick={handleAddIngredient}
                  className="px-3 py-1 bg-green-500 text-white text-sm rounded-md hover:bg-green-600"
                >
                  + 材料追加
                </button>
              </div>
              
              {currentRecipe.ingredients.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  まだ材料が登録されていません
                </p>
              ) : (
                <div className="space-y-2">
                  {currentRecipe.ingredients.map((ingredient, index) => (
                    <div key={index} className="flex gap-3 items-center p-3 border rounded-lg">
                      <div className="flex-1">
                        <select
                          value={ingredient.inventoryId}
                          onChange={(e) => handleUpdateIngredient(index, 'inventoryId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">材料を選択</option>
                          {inventoryItems.map(item => (
                            <option key={item.id} value={item.id}>
                              {item.item_name} ({item.unit})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="w-24">
                        <input
                          type="number"
                          value={ingredient.quantity}
                          onChange={(e) => handleUpdateIngredient(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="数量"
                          step="0.1"
                          min="0"
                        />
                      </div>
                      
                      <div className="w-16 text-sm text-gray-600">
                        {ingredient.unit || '-'}
                      </div>
                      
                      <button
                        onClick={() => handleRemoveIngredient(index)}
                        className="px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowRecipeModal(false);
                  setCurrentRecipe({ menuId: '', ingredients: [] });
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveRecipe}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                レシピ保存
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </PageContainer>
  );
};

export default MenuManagement;